import { useState } from 'react'
import toast from 'react-hot-toast'
import { usersAPI } from '../../api/users'

interface Props {
  userId:   string
  userName: string
  onClose:  () => void
}

function generatePassword(): string {
  const chars = 'abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ23456789'
  const specials = '!@#$%'
  let pw = ''
  for (let i = 0; i < 9; i++) pw += chars[Math.floor(Math.random() * chars.length)]
  pw += specials[Math.floor(Math.random() * specials.length)]
  return pw
}

export default function ChangePasswordModal({ userId, userName, onClose }: Props) {
  const [password, setPassword]   = useState('')
  const [confirm, setConfirm]     = useState('')
  const [showPw, setShowPw]       = useState(false)
  const [loading, setLoading]     = useState(false)
  const [copied, setCopied]       = useState(false)

  const mismatch = confirm.length > 0 && password !== confirm
  const tooShort = password.length > 0 && password.length < 8
  const canSubmit = password.length >= 8 && password === confirm

  const handleGenerate = () => {
    const pw = generatePassword()
    setPassword(pw)
    setConfirm(pw)
    setShowPw(true)
    setCopied(false)
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(password)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return
    setLoading(true)
    try {
      await usersAPI.changePassword(userId, password)
      toast.success(`Mot de passe de ${userName} mis à jour`)
      onClose()
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
      toast.error(msg ?? 'Erreur lors du changement de mot de passe')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)',
        backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 9999, padding: 20,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff', borderRadius: 20, padding: 28,
          width: '100%', maxWidth: 420,
          boxShadow: '0 24px 64px rgba(0,0,0,.18)',
          animation: 'pwModalIn .2s ease',
        }}
        onClick={e => e.stopPropagation()}
      >
        <style>{`@keyframes pwModalIn { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:none; } }`}</style>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 22 }}>
          <div style={{
            width: 44, height: 44, borderRadius: 12, flexShrink: 0,
            background: '#EDE9FE', display: 'grid', placeItems: 'center', fontSize: 20,
          }}>🔑</div>
          <div>
            <h3 style={{ margin: '0 0 4px', fontSize: 17, fontWeight: 700, color: '#0F172A' }}>
              Changer le mot de passe
            </h3>
            <p style={{ margin: 0, fontSize: 13, color: '#64748B' }}>{userName}</p>
          </div>
          <button
            onClick={onClose}
            style={{
              marginLeft: 'auto', width: 30, height: 30, borderRadius: 8,
              border: '1px solid #E2E8F0', background: '#fff',
              cursor: 'pointer', fontSize: 14, color: '#94A3B8',
              display: 'grid', placeItems: 'center',
            }}
          >✕</button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Nouveau mot de passe */}
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.05em' }}>
              Nouveau mot de passe
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={e => { setPassword(e.target.value); setCopied(false) }}
                placeholder="Min. 8 caractères"
                style={{
                  width: '100%', padding: '11px 44px 11px 14px', borderRadius: 10,
                  border: `1px solid ${tooShort ? '#FCA5A5' : '#E2E8F0'}`,
                  fontSize: 14, outline: 'none', fontFamily: password ? 'JetBrains Mono, monospace' : 'inherit',
                  letterSpacing: password ? '.06em' : 'normal',
                  boxSizing: 'border-box', color: '#0F172A',
                }}
              />
              <button
                type="button"
                onClick={() => setShowPw(v => !v)}
                style={{
                  position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: 15, color: '#94A3B8', padding: 4,
                }}
              >{showPw ? '🙈' : '👁'}</button>
            </div>
            {tooShort && (
              <p style={{ margin: '5px 0 0', fontSize: 12, color: '#EF4444' }}>
                Au moins 8 caractères requis
              </p>
            )}
          </div>

          {/* Confirmer */}
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.05em' }}>
              Confirmer le mot de passe
            </label>
            <input
              type={showPw ? 'text' : 'password'}
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              placeholder="Répéter le mot de passe"
              style={{
                width: '100%', padding: '11px 14px', borderRadius: 10,
                border: `1px solid ${mismatch ? '#FCA5A5' : '#E2E8F0'}`,
                fontSize: 14, outline: 'none',
                fontFamily: confirm ? 'JetBrains Mono, monospace' : 'inherit',
                letterSpacing: confirm ? '.06em' : 'normal',
                boxSizing: 'border-box', color: '#0F172A',
              }}
            />
            {mismatch && (
              <p style={{ margin: '5px 0 0', fontSize: 12, color: '#EF4444' }}>
                Les mots de passe ne correspondent pas
              </p>
            )}
          </div>

          {/* Séparateur */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ flex: 1, height: 1, background: '#E2E8F0' }} />
            <span style={{ fontSize: 12, color: '#94A3B8', fontWeight: 500 }}>ou</span>
            <div style={{ flex: 1, height: 1, background: '#E2E8F0' }} />
          </div>

          {/* Générer automatiquement */}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              onClick={handleGenerate}
              style={{
                flex: 1, padding: '10px 14px', borderRadius: 10,
                border: '1px solid #DDD6FE', background: '#F5F3FF',
                color: '#7C3AED', fontWeight: 600, fontSize: 13,
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              }}
            >
              🎲 Générer automatiquement
            </button>
            {password && (
              <button
                type="button"
                onClick={handleCopy}
                style={{
                  padding: '10px 14px', borderRadius: 10,
                  border: '1px solid #E2E8F0',
                  background: copied ? '#D1FAE5' : '#F8FAFC',
                  color: copied ? '#065F46' : '#374151',
                  fontWeight: 600, fontSize: 13, cursor: 'pointer',
                  transition: 'all .2s', whiteSpace: 'nowrap',
                }}
              >
                {copied ? '✓ Copié' : 'Copier'}
              </button>
            )}
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                flex: 1, padding: '11px', borderRadius: 10,
                border: '1px solid #E2E8F0', background: '#fff',
                fontSize: 14, fontWeight: 500, cursor: 'pointer', color: '#374151',
              }}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={!canSubmit || loading}
              style={{
                flex: 1, padding: '11px', borderRadius: 10, border: 0,
                background: canSubmit ? '#7C3AED' : '#E2E8F0',
                color: canSubmit ? '#fff' : '#94A3B8',
                fontSize: 14, fontWeight: 700,
                cursor: canSubmit ? 'pointer' : 'not-allowed',
                opacity: loading ? 0.7 : 1,
                transition: 'all .2s',
              }}
            >
              {loading ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
